import React, { useState } from 'react';
import {
    AlertTriangle, ArrowRight, ArrowLeft, CheckCircle2, UserMinus, UserPlus,
    RotateCcw, Link2, Scale, RefreshCw, Info,
} from 'lucide-react';
import ModalArbitrage from './ModalArbitrage';

/**
 * Étape 3 : le rapport du dry-run en paniers (spec §10.3) + arbitrages.
 * Ordre d'affichage voulu par la spec : le panier d'alerte « sortants liés à un
 * compte ENT » d'abord (jamais noyé), puis les gardes, puis les paniers
 * lisibles, puis les confirmations/arbitrages.
 *
 * Bloc « arbitrages » (refonte) : une liste unique groupée par famille de cas,
 * une seule question par ligne, un segmented control toujours au même endroit.
 * Les libellés d'options sont IDENTIQUES d'un groupe à l'autre (« Même élève » /
 * « 2 élèves », + « Je ne sais pas » sur les collisions d'identité) ; la phrase
 * d'explication vit au niveau du GROUPE, jamais répétée par ligne ; rien n'est
 * pré-coché (« Tout confirmer » par groupe applique la réponse recommandée aux
 * cas sans réponse). Les doublons fichier sont de simples notifications,
 * repliées, hors compteur de décisions.
 */

// Familles de suspects, dans l'ordre d'affichage. `reco` = la réponse posée par
// « Tout confirmer ». La réconciliation se fait par nom — aucune donnée
// d'état civil (naissance, sexe) n'est collectée ni rapportée ; chaque carte
// suspect porte un score de ressemblance.
// doublon_fichier n'a PAS de groupe décisionnel : le back garde la première
// occurrence, ignore la ligne dupliquée ET toute résolution envoyée dessus.
// Simple notification repliée. Tous les autres types gardent leurs résolutions.

// ─── Normalisation des cas d'arbitrage (servie au modal « un cas par un ») ───
//
// Chaque cas = { id, groupe, typeLabel, titre, sousTitre, diffNom,
// concordances, sujetPill, faits, options[], valeur, repondu, produitAction,
// repondre }. Les OPTIONS sont des RÉCITS (titreRecit au passé, effets en
// puces, note, badge de vraisemblance) ; une option interdite porte sa raison
// écrite (jamais un simple refus serveur). Les jumelles (retour_ambigu /
// deplacement_aveugle) sont FUSIONNÉES en un seul cas : répondre dispatch
// associer sur la ligne élue et creer sur les sœurs.

/** « de Noah » / « d'Emma » : l'élision devant voyelle ou h. */
const de = (prenom) => (/^[aeiouyhàâäéèêëîïôöùûüAEIOUYHÀÂÄÉÈÊËÎÏÔÖÙÛÜ]/.test(prenom) ? `d'${prenom}` : `de ${prenom}`);

// « Décider plus tard » n'existe que sur les CONFLITS, où c'est une vraie
// réponse (la fiche est protégée, la question revient au prochain import).
// Sur un suspect ou un changement de classe, ce serait une impasse : le verrou
// interdit de valider sans réponse (épreuve de Musk 2026-09-12).
const OPTION_PLUS_TARD_CONFLIT = {
    value: '', label: 'Décider plus tard', court: 'Plus tard',
    consequence: 'Rien ne sera fait pour ce cas : la fiche est protégée, la question reviendra au prochain import.',
};

/** Diff préfixe/suffixe entre deux chaînes (pour le rendu lettre à lettre). */
export const diffChaines = (avant, apres) => {
    let p = 0;
    while (p < avant.length && p < apres.length && avant[p] === apres[p]) p++;
    let sfx = 0;
    while (sfx < avant.length - p && sfx < apres.length - p
        && avant[avant.length - 1 - sfx] === apres[apres.length - 1 - sfx]) sfx++;
    return {
        prefixe: avant.slice(0, p),
        retire: avant.slice(p, avant.length - sfx),
        ajoute: apres.slice(p, apres.length - sfx),
        suffixe: sfx ? avant.slice(avant.length - sfx) : '',
    };
};

/** Construit la liste normalisée des cas d'arbitrage du rapport.
 *  Exporté pour les tests (comme diffChaines) : c'est ici que vit tout le
 *  routage type/sous_cas → récits du contrat 2026-08. */
export const construireCasArbitrage = ({
    rapport, resolutionsSuspects, resolutionsConflits,
    onResoudreSuspect, onResoudreConflit,
    resolutionsDeplacements = {}, onResoudreDeplacement,
}) => {
    const cas = [];

    // ── Suspects ──
    for (const s of rapport.suspects) {
        const memeClasse = s.creation.classe === s.sortie.classe;
        const prenom = s.creation.prenom;
        const valeur = resolutionsSuspects[s.paire_id] || '';

        // Concordances : les VRAIES valeurs côté fichier (3 colonnes, la
        // réconciliation se fait par nom — la date de naissance et le sexe ne
        // sont plus collectés ni rapportés).
        // Ressemblance : le back fournit désormais s.score sur TOUTES les
        // cartes suspects — affiché partout (décision utilisateur 2026-08-16).
        const cScore = { label: 'Ressemblance', valeur: `${Math.round((s.score || 0) * 100)} %`, etat: 'ok' };
        const cClasse = memeClasse
            ? { label: 'Classe', valeur: s.creation.classe, etat: 'ok' }
            : { label: 'Classe', valeur: `fichier ${s.creation.classe} · TanoEd ${s.sortie.classe}`, etat: 'diff' };
        const cPrenom = { label: 'Prénom', valeur: prenom, etat: s.creation.prenom === s.sortie.prenom ? 'ok' : 'diff' };

        const d = diffChaines(s.sortie.nom, s.creation.nom);
        const nbLettres = d.retire.length + d.ajoute.length;
        // Identité complète dans le titre (retour user) : nom actuel TanoEd +
        // classe — et TOUJOURS une question, c'est le prof qui tranche.
        const qui = `${prenom} ${s.sortie.nom} (${s.creation.classe})`;

        // Récit « remplacement » (le rare) : un élève sort, un autre entre.
        const recitRemplacement = (sousRecit, extra = {}) => ({
            value: 'deux_eleves', court: '2 élèves',
            badge: 'rare',
            titreRecit: 'Un élève en a remplacé un autre',
            sousRecit,
            effets: [
                `${prenom} ${s.sortie.nom} quitte l'établissement (à confirmer ensuite)`,
                `${prenom} ${s.creation.nom} arrive en ${s.creation.classe}`,
            ],
            ...extra,
        });

        // Types nommés émis par le back : orthographe et amorcage_orpheline ;
        // tout type inconnu retombe sur le fallback générique dessous.
        const variantes = {
            orthographe: {
                typeLabel: 'Correction de nom',
                titre: `Le nom ${de(qui)} a changé ${nbLettres === 1 ? 'd\'une lettre' : 'd\'écriture'} ?`,
                sousTitre: null,
                diffNom: { avant: s.sortie.nom, apres: s.creation.nom },
                concordances: [cScore, cClasse, cPrenom],
                options: [
                    {
                        value: 'fusionner', court: 'Corriger',
                        badge: 'probable',
                        titreRecit: 'Le nom a été corrigé',
                        effets: [
                            `${prenom} devient ${s.creation.nom}`,
                            'Ses notes sont conservées',
                            ...(memeClasse ? [] : [`${prenom} passe de ${s.sortie.classe} en ${s.creation.classe}`]),
                        ],
                    },
                    recitRemplacement(`Deux ${prenom}${memeClasse ? ' dans la même classe' : ' qui se ressemblent'}.`),
                ],
            },
            amorcage_orpheline: {
                typeLabel: 'Compte ENT à rattacher',
                titre: `Ce compte ENT est-il celui de ${qui} ?`,
                sousTitre: 'Une fiche a été créée quand un élève s\'est connecté via l\'ENT : elle ressemble à cette ligne de votre fichier.',
                diffNom: s.creation.nom !== s.sortie.nom ? { avant: s.sortie.nom, apres: s.creation.nom } : null,
                concordances: [cScore, cClasse],
                options: [
                    {
                        value: 'fusionner', court: 'Rattacher',
                        badge: 'probable',
                        titreRecit: `${prenom} s'est connecté avant d'être importé`,
                        sousRecit: 'Le compte ENT et la ligne du fichier sont la même personne.',
                        effets: [
                            `Une seule fiche pour ${prenom}`,
                            'Son travail en ligne est conservé',
                        ],
                    },
                    {
                        value: 'deux_eleves', court: '2 élèves',
                        badge: 'rare',
                        titreRecit: 'C\'est un autre élève',
                        sousRecit: 'La ressemblance est une coïncidence.',
                        effets: [
                            `${prenom} est créé en ${s.creation.classe}`,
                            'La fiche créée par l\'ENT sera proposée sortante (à confirmer ensuite)',
                        ],
                    },
                ],
            },
        };
        const v = variantes[s.type] || {
            typeLabel: 'Correspondance à vérifier',
            titre: 'Est-ce le même élève ?',
            sousTitre: 'Une ligne de votre fichier ressemble à une fiche TanoEd.',
            diffNom: s.creation.nom !== s.sortie.nom ? { avant: s.sortie.nom, apres: s.creation.nom } : null,
            concordances: [cScore, cClasse, cPrenom],
            options: [
                {
                    value: 'fusionner', court: 'Même élève',
                    titreRecit: 'C\'est la même personne',
                    effets: ['Une seule fiche, notes conservées'],
                },
                recitRemplacement('Deux élèves qui se ressemblent.', { badge: undefined }),
            ],
        };
        cas.push({
            id: `suspect:${s.paire_id}`,
            groupe: s.type,
            typeLabel: v.typeLabel,
            titre: v.titre,
            sousTitre: v.sousTitre,
            diffNom: v.diffNom,
            concordances: v.concordances,
            sujetPill: `${prenom} ${s.creation.nom} · ${s.creation.classe}`,
            question: v.titre,
            sujet: `${s.creation.nom} ${s.creation.prenom}`,
            entLie: !!s.sortie.liee_gar,
            faits: memeClasse ? `classe ${s.creation.classe}` : `classe ${s.sortie.classe} → ${s.creation.classe}`,
            options: v.options,
            valeur,
            repondu: !!valeur,
            produitAction: !!valeur,
            repondre: (val) => onResoudreSuspect(s.paire_id, val),
        });
    }

    // ── Changements de classe reconnus PAR LE NOM ──
    // Même décision qu'un suspect (« le même élève, ou un autre ? »), même
    // ambiguïté (une fiche libre, une ligne orpheline, même niveau) : le
    // moteur PARIE le déplacement quand le nom est unique, et le référent le
    // confirme ou le contredit ici, avec les mêmes deux réponses que partout.
    // Un déplacement prouvé par l'INE ne se discute pas (pas de cas).
    // Valeurs front : 'meme' (répondu, rien à envoyer), 'creer' (nouveau +
    // sortant, part en deplacements_resolus), '' (plus tard).
    for (const d of (rapport.deplaces || []).filter((x) => x.via === 'nom')) {
        const valeur = resolutionsDeplacements[String(d.ligne)] || '';
        const prenomNom = `${d.prenom} ${d.nom}`;
        cas.push({
            id: `deplacement:${d.ligne}`,
            groupe: 'deplacement',
            typeLabel: 'Changement de classe',
            titre: `${prenomNom} est passé de ${d.de_classe} en ${d.vers_classe} ?`,
            sousTitre: null,
            diffNom: null,
            concordances: [],
            sujetPill: `${prenomNom} · ${d.vers_classe}`,
            question: `${prenomNom} : le même élève ?`,
            sujet: `${d.nom} ${d.prenom}`,
            entLie: false,
            faits: `classe ${d.de_classe} → ${d.vers_classe}`,
            options: [
                {
                    value: 'meme', court: 'Même élève',
                    badge: 'probable',
                    titreRecit: `${d.prenom} a changé de classe`,
                    // le prénom, jamais « il » : le sexe n'est pas collecté
                    effets: [`${d.prenom} garde ses notes.`],
                },
                {
                    value: 'creer', court: '2 élèves',
                    badge: 'rare',
                    titreRecit: 'Un élève en a remplacé un autre',
                    effets: [
                        `${prenomNom} de ${d.de_classe} quitte l'établissement (à confirmer ensuite)`,
                        `${prenomNom} arrive en ${d.vers_classe}`,
                    ],
                },
            ],
            valeur,
            repondu: !!valeur,
            produitAction: valeur === 'creer',
            repondre: (val) => onResoudreDeplacement(d.ligne, val),
        });
    }

    // ── Collisions (sous_cas) et multi ──
    const conflits = rapport.conflits.filter((x) => x.type !== 'doublon_fichier');
    const dejaFusionnes = new Set();
    for (const cf of conflits) {
        const cle = String(cf.ligne);
        if (dejaFusionnes.has(cle)) continue;
        const candidats = cf.candidats || [];
        const sousCas = cf.type === 'collision_identite' ? cf.sous_cas : null;
        const valeurBrute = resolutionsConflits[cle];

        // Jumelles fusionnées : plusieurs conflits citant LA MÊME fiche candidate.
        if ((sousCas === 'retour_ambigu' || sousCas === 'deplacement_aveugle') && candidats.length === 1) {
            const fiche = candidats[0];
            const freres = conflits.filter((x) =>
                x.type === 'collision_identite'
                && (x.sous_cas === sousCas)
                && (x.candidats || []).length === 1
                && x.candidats[0].fiche_id === fiche.fiche_id);
            freres.forEach((f) => dejaFusionnes.add(String(f.ligne)));
            const retour = sousCas === 'retour_ambigu';
            const valeurs = freres.map((f) => resolutionsConflits[String(f.ligne)]);
            const elu = freres.find((f) => (resolutionsConflits[String(f.ligne)] || '').startsWith('associer'));
            const valeur = elu ? String(elu.ligne)
                : valeurs.every((v) => v === 'creer') ? 'aucune'
                    : valeurs.every((v) => v === '') ? '' : undefined;
            cas.push({
                id: `fusion:${fiche.fiche_id}`,
                groupe: sousCas,
                typeLabel: retour ? 'Retour à préciser' : 'Déménagement à préciser',
                titre: retour
                    ? `${cf.prenom} ${cf.nom} (fiche archivée en ${fiche.classe}) est de retour ?`
                    : `Laquelle de ces lignes est ${cf.prenom} ${cf.nom} (${fiche.classe}) ?`,
                sousTitre: retour
                    ? `${cf.prenom} avait quitté l'établissement (fiche archivée en ${fiche.classe}). ${freres.length} lignes du fichier portent son identité exacte : son ancienne classe suggère, elle ne prouve rien.`
                    : `Une seule fiche existe (active en ${fiche.classe}), mais ${freres.length} lignes identiques visent d'autres classes. Deviner dépendrait de l'ordre du fichier : sa fiche est protégée tant que vous n'avez pas répondu.`,
                diffNom: null,
                concordances: [],
                sujetPill: `${cf.prenom} ${cf.nom} · ${fiche.classe}`,
                question: retour ? `${cf.prenom} ${cf.nom} : de retour ?` : `Laquelle de ces lignes est ${cf.prenom} ${cf.nom} ?`,
                sujet: `${cf.nom} ${cf.prenom}`,
                entLie: false,
                faits: `fiche ${retour ? 'archivée' : 'active'} en ${fiche.classe} · lignes en ${freres.map((f) => f.classe).join(', ')}`,
                options: [
                    // Histoires symétriques entre les classes : AUCUN badge.
                    ...freres.map((f) => ({
                        value: String(f.ligne),
                        court: f.classe,
                        titreRecit: retour
                            ? `${cf.prenom} est revenu en ${f.classe}`
                            : `${cf.prenom} est maintenant en ${f.classe}`,
                        effets: [
                            retour
                                ? `Sa fiche est réactivée en ${f.classe}`
                                : `Sa fiche passe de ${fiche.classe} en ${f.classe}`,
                            'Tout son historique est conservé',
                            freres.length > 1 ? 'Les autres lignes créent de nouveaux élèves' : null,
                        ].filter(Boolean),
                    })),
                    {
                        value: 'aucune',
                        court: 'Aucune',
                        badge: 'rare',
                        titreRecit: retour
                            ? `${cf.prenom} n'est jamais revenu`
                            : `${cf.prenom} est parti`,
                        sousRecit: `Ces ${freres.length} lignes sont d'autres élèves.`,
                        effets: [
                            `${freres.length} nouvelles fiches sont créées`,
                            retour
                                ? 'La fiche archivée reste en sommeil'
                                : `${cf.prenom} de ${fiche.classe} quitte l'établissement (à confirmer ensuite)`,
                        ],
                    },
                    OPTION_PLUS_TARD_CONFLIT,
                ],
                valeur,
                repondu: valeurs.every((v) => v !== undefined),
                produitAction: !!elu || valeurs.every((v) => v === 'creer'),
                repondre: (v) => {
                    freres.forEach((f) => {
                        const k = String(f.ligne);
                        if (v === '') onResoudreConflit(k, '');
                        else if (v === 'aucune') onResoudreConflit(k, 'creer');
                        else onResoudreConflit(k, k === v ? `associer:${fiche.fiche_id}` : 'creer');
                    });
                },
            });
            continue;
        }

        // Chassé-croisé actif / archivé : seule la réactivation est répondable.
        if (sousCas === 'actif_vs_dormante' && candidats.length >= 2) {
            const dormante = candidats.find((x) => x.inactive) || candidats[1];
            const actif = candidats.find((x) => !x.inactive) || candidats[0];
            cas.push({
                id: `conflit:${cle}`,
                groupe: 'actif_vs_dormante',
                typeLabel: 'Chassé-croisé',
                titre: `Qui est le vrai ${cf.prenom} ${cf.nom} (${cf.classe}) ?`,
                sousTitre: `Récit A : l'élève actif de ${actif.classe} déménage en ${cf.classe}. Récit B : l'ancien de ${cf.classe} revient chez lui, et celui de ${actif.classe} part. La ligne vise la place de la fiche archivée : seul le récit B est possible.`,
                diffNom: null,
                concordances: [],
                sujetPill: `${cf.prenom} ${cf.nom} · ${cf.classe}`,
                question: `Qui est le vrai ${cf.prenom} ${cf.nom} ?`,
                sujet: `${cf.nom} ${cf.prenom}`,
                entLie: false,
                faits: `actif en ${actif.classe} · archivé en ${dormante.classe} · la ligne vise ${cf.classe}`,
                options: [
                    {
                        value: `associer:${dormante.fiche_id}`,
                        court: 'Retour de l\'ancien',
                        badge: 'probable',
                        titreRecit: `L'ancien ${cf.prenom} est revenu, l'actuel est parti`,
                        sousRecit: 'Le récit B : chacun retrouve sa place.',
                        effets: [
                            `La fiche archivée de ${cf.classe} est réactivée`,
                            `${cf.prenom} de ${actif.classe} quitte l'établissement (à confirmer ensuite)`,
                        ],
                        note: 'Chaque historique reste sur sa fiche',
                    },
                    {
                        value: `associer:${actif.fiche_id}`,
                        court: 'Déménagement',
                        titreRecit: `L'actuel a déménagé en ${cf.classe}`,
                        desactive: true,
                        raison: 'Refusé par le moteur : la fiche archivée occupe déjà cette place — ce récit décrirait un état impossible.',
                    },
                    {
                        value: 'creer',
                        court: 'Nouvel élève',
                        titreRecit: 'Un nouvel élève identique est arrivé',
                        desactive: true,
                        raison: 'Refusé par le moteur : la place visée est occupée par la fiche archivée. Contactez le support si nécessaire.',
                    },
                    OPTION_PLUS_TARD_CONFLIT,
                ],
                valeur: valeurBrute,
                repondu: valeurBrute !== undefined,
                produitAction: !!valeurBrute,
                repondre: (v) => onResoudreConflit(cle, v),
            });
            continue;
        }

        // Homonymes parfaits / collision générique 1 candidat / multi-fiches.
        const estHomonyme = sousCas === 'homonyme_actif';
        const estMulti = !sousCas && candidats.length > 1;
        cas.push({
            id: `conflit:${cle}`,
            groupe: estHomonyme ? 'homonyme_actif' : estMulti || cf.type !== 'collision_identite' ? 'multi' : 'collision',
            typeLabel: estHomonyme ? 'Homonymes parfaits' : 'Plusieurs fiches',
            titre: estHomonyme
                ? `${cf.prenom} ${cf.nom} arrive en ${cf.classe} : le même ou un autre ?`
                : `Quelle fiche est ${cf.prenom} ${cf.nom} (${cf.classe}) ?`,
            sousTitre: estHomonyme
                ? `${candidats.length === 1 ? `Un élève au nom et prénom strictement identiques existe déjà en ${candidats[0].classe}` : `${candidats.length} élèves strictement identiques existent déjà`} : déménagement, ou véritable homonyme de plus ?`
                : `${candidats.length} fiche${candidats.length > 1 ? 's' : ''} TanoEd ${candidats.length > 1 ? 'peuvent' : 'peut'} correspondre à cette ligne (${cf.classe}).${cf.detail ? ` ${cf.detail}.` : ''}`,
            diffNom: null,
            concordances: [],
            sujetPill: `${cf.prenom} ${cf.nom} · ${cf.classe}`,
            question: estHomonyme ? `Qui arrive en ${cf.classe} ?` : `Quelle fiche est ${cf.prenom} ${cf.nom} ?`,
            sujet: `${cf.nom} ${cf.prenom}`,
            entLie: false,
            faits: `ligne en ${cf.classe} · candidat${candidats.length > 1 ? 's' : ''} : ${candidats.map((x) => `${x.classe}${x.inactive ? ' (archivé)' : ''}`).join(', ')}`,
            options: [
                // Le déménagement est le pari du moteur : badge « probable »
                // seulement à candidat unique (plusieurs = histoires symétriques).
                ...candidats.map((cand) => ({
                    value: `associer:${cand.fiche_id}`,
                    court: cand.classe,
                    badge: estHomonyme && candidats.length === 1 ? 'probable' : undefined,
                    titreRecit: cand.inactive
                        ? `${cf.prenom} de ${cand.classe} est de retour`
                        : `${cf.prenom} a changé de classe (${cand.classe} vers ${cf.classe})`,
                    effets: [
                        cand.inactive
                            ? `Sa fiche archivée est réactivée en ${cf.classe}`
                            : `Sa fiche passe de ${cand.classe} en ${cf.classe}`,
                        'Notes et absences conservées',
                    ],
                    note: estHomonyme ? 'Effectif total inchangé' : undefined,
                })),
                {
                    value: 'creer',
                    court: 'Nouvelle fiche',
                    badge: estHomonyme && candidats.length === 1 ? 'rare' : undefined,
                    titreRecit: estHomonyme
                        ? `Un ${cf.prenom} de plus est arrivé`
                        : 'C\'est un nouvel élève',
                    sousRecit: estHomonyme ? 'Un véritable homonyme supplémentaire.' : undefined,
                    effets: [
                        `Une nouvelle fiche naît en ${cf.classe}`,
                        estHomonyme && candidats.length === 1
                            ? `${cf.prenom} de ${candidats[0].classe} quitte l'établissement (à confirmer ensuite)`
                            : null,
                    ].filter(Boolean),
                },
                ...(estMulti || cf.type !== 'collision_identite' ? [{
                    value: 'ignorer',
                    court: 'Ignorer',
                    titreRecit: 'Cette ligne est une erreur',
                    effets: ['La ligne est écartée : rien n\'est créé ni modifié'],
                }] : []),
                OPTION_PLUS_TARD_CONFLIT,
            ],
            valeur: valeurBrute,
            repondu: valeurBrute !== undefined,
            produitAction: !!valeurBrute,
            repondre: (v) => onResoudreConflit(cle, v),
        });
    }
    return cas;
};

// Deux états visuels seulement : `neutre` (info, lecture seule) et `attention`
// (décision attendue). Le danger a ses bandeaux rouges dédiés (hors Panier).
// `iconeClasse` permet de teinter l'icône d'une carte neutre sans colorer le fond.
// `repliable` : panier purement consultatif — replié par défaut sur son seul
// compteur, le détail s'ouvre avec « Voir » (les paniers de décision restent
// toujours ouverts).
// Le MÊME repère d'état sur chaque bloc qui attend une réponse : « à confirmer »
// en orange, « ✓ … » en vert (le bloc des cas l'affiche déjà, on l'aligne).
function Etat({ fait, libelleFait = '✓ confirmé' }) {
    return fait ? (
        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{libelleFait}</span>
    ) : (
        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">à confirmer</span>
    );
}

function Panier({ icone: Icone, titre, teinte, children, compte, iconeClasse, repliable, etat }) {
    const [ouvert, setOuvert] = useState(false);
    const montre = !repliable || ouvert;
    const teintes = {
        neutre: 'bg-slate-50 border-slate-200 text-slate-700',
        attention: 'bg-amber-50 border-amber-200 text-amber-800',
    };
    return (
        <div className={`border rounded-xl p-4 ${teintes[teinte]}`}>
            <p className={`text-sm font-semibold flex items-center gap-2 ${montre ? 'mb-2' : ''}`}>
                <Icone className={`w-4 h-4 ${iconeClasse || ''}`} />
                {titre}
                {compte !== undefined ? (
                    <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full bg-white/70">{compte}</span>
                ) : null}
                {etat !== undefined ? <Etat fait={etat} /> : null}
                {repliable ? (
                    <button
                        onClick={() => setOuvert((o) => !o)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                    >
                        {ouvert ? 'Masquer' : 'Voir'}
                    </button>
                ) : null}
            </p>
            {montre ? children : null}
        </div>
    );
}


function EtapeRapport({
    rapport, lignes = [],
    resolutionsConflits, onResoudreConflit,
    resolutionsSuspects, onResoudreSuspect,
    resolutionsDeplacements = {}, onResoudreDeplacement,
    sortantsCoches, setSortantsCoches,
    rapportPerime, suspectsIntegres = {}, onReverifier,
    onAppliquer, onRetour, onRecommencer,
    chargement, erreur,
}) {
    const [voirDoublons, setVoirDoublons] = useState(false);

    const sortantsLies = rapport.sortants.filter((s) => s.liee_gar);
    // Les gardes du back arrivent AUSSI dans avertissements → on les filtre
    // (elles ont leur bannière dédiée, sinon le prof lit deux fois la même chose)
    const avertissementsFiltres = (rapport.avertissements || []).filter((a) =>
        !a.includes('fichier probablement partiel') && !a.includes('Dépassement de la limite'));
    // Porte bloquante : au moins une ligne malformée → SEUL ce panneau s'affiche
    // (pas de prévisualisation : l'utilisateur doit corriger le fichier Excel et
    // ré-importer de toute façon, de zéro — les numéros de lignes changent,
    // aucune résolution ne survit).
    const importBloque = avertissementsFiltres.length > 0;

    // Découpage : notifications (doublons fichier) hors décisions ; le reste
    // est NORMALISÉ en « cas d'arbitrage » (jumelles fusionnées, options avec
    // conséquences nominatives) — servi au modal ET aux groupes inline.
    const doublonsFichier = rapport.conflits.filter((c) => c.type === 'doublon_fichier');
    const conflitsDecision = rapport.conflits.filter((c) => c.type !== 'doublon_fichier');
    const casArbitrage = construireCasArbitrage({
        rapport, resolutionsSuspects, resolutionsConflits,
        onResoudreSuspect, onResoudreConflit,
        resolutionsDeplacements, onResoudreDeplacement,
    });
    const [modalOuvert, setModalOuvert] = useState(false);

    const nbDecisions = casArbitrage.length;
    // Compté pour le LIBELLÉ du bouton Valider : les réponses qui produisent
    // une action (« plus tard » n'en produit pas).
    const nbRepondus = casArbitrage.filter((c) => c.produitAction).length;
    // VERROU (décision user) : tout doit être arbitré avant de re-vérifier ou
    // valider. « Plus tard » sur un conflit (clé posée, valeur vide) COMPTE
    // comme une réponse : décision explicite de laisser le cas en attente.
    const resteAArbitrer = casArbitrage.filter((c) => !c.repondu).length;
    // Les changements de classe par le nom sont des cas comme les autres ; en
    // septembre ils sont nombreux et presque tous vrais : un bouton répond
    // « Même élève » à ceux qui restent, le modal sert à les voir un par un.
    const deplacementsSansReponse = casArbitrage.filter((c) => c.groupe === 'deplacement' && !c.repondu);
    const confirmerDeplacements = () => deplacementsSansReponse.forEach((c) => c.repondre('meme'));
    // Le panneau « Changements de classe » ne montre que ce qui n'est PAS un
    // cas : les déplacements prouvés par l'INE. Les autres sont déjà dans les
    // cas à confirmer, avec leur réponse ; les y répéter ne dirait rien de plus.
    const deplacesSurs = rapport.deplaces.filter((d) => d.via !== 'nom');
    const applyBloque = chargement || resteAArbitrer > 0
        || (rapport.sortants.length > 0 && !sortantsCoches);

    const partiesValider = [];
    if (nbRepondus > 0) partiesValider.push(`${nbRepondus} arbitrage${nbRepondus > 1 ? 's' : ''}`);
    if (rapport.sortants.length > 0) partiesValider.push(`${rapport.sortants.length} sortie${rapport.sortants.length > 1 ? 's' : ''}`);
    const libelleValider = partiesValider.length > 0 ? `Valider · ${partiesValider.join(', ')}` : 'Valider l\'import';

    // Ajustement des compteurs : les réponses « suspects » pas encore passées
    // par un re-dry-run (absentes du snapshot intégré) déplacent les paniers —
    // « 2 élèves » ajoute un créé ET un sortant, « Même élève » un changement
    // de classe (ou un à-jour si les classes coïncident). Les chiffres exacts
    // reviennent du re-dry-run, obligatoire tant que le rapport est périmé.
    const deltas = rapport.suspects.reduce((acc, s) => {
        const choix = resolutionsSuspects[s.paire_id];
        if (!choix || suspectsIntegres[s.paire_id] === choix) return acc;
        if (choix === 'deux_eleves') { acc.crees += 1; acc.sortants += 1; }
        else if (s.creation.classe === s.sortie.classe) acc.inchanges += 1;
        else acc.deplaces += 1;
        return acc;
    }, { inchanges: 0, deplaces: 0, crees: 0, sortants: 0 });

    const blocDoublons = doublonsFichier.length > 0 ? (
        <div className="mb-2">
            <p className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
                <Info className="w-3.5 h-3.5 flex-shrink-0" />
                {doublonsFichier.length} ligne{doublonsFichier.length > 1 ? 's' : ''} en double ignorée{doublonsFichier.length > 1 ? 's' : ''} automatiquement
                <button
                    onClick={() => setVoirDoublons((v) => !v)}
                    className="text-blue-600 hover:text-blue-800 font-semibold"
                >
                    {voirDoublons ? 'Masquer' : 'Voir'}
                </button>
            </p>
            {voirDoublons ? (
                <ul className="text-[12px] text-slate-500 mt-1 space-y-0.5 pl-5">
                    {doublonsFichier.map((c) => (
                        <li key={c.ligne}>
                            Ligne {c.ligne + 1} : {c.detail} ({c.nom} {c.prenom}, {c.classe}). L'élève sera importé
                            une seule fois ; si ce n'est pas voulu, corrigez le fichier.
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    ) : null;

    // Rien à faire : le fichier correspond déjà exactement à TanoEd (que des
    // inchangés). Les doublons fichier ignorés ne comptent PAS comme une action
    // (aucune décision, aucune écriture) : l'établissement reste « à jour »,
    // la note sur les lignes ignorées est simplement affichée dessous.
    const rienAFaire = rapport.deplaces.length === 0
        && rapport.crees.length === 0
        && rapport.reactives.length === 0
        && rapport.sortants.length === 0
        && conflitsDecision.length === 0
        && rapport.suspects.length === 0
        && avertissementsFiltres.length === 0;

    if (rienAFaire) {
        return (
            <div className="space-y-6">
                <div className="text-center py-6">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-emerald-500 flex items-center justify-center">
                        <CheckCircle2 className="w-9 h-9 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-700 mb-1">L'établissement est déjà à jour</h3>
                    <p className="text-sm text-slate-500">
                        Les {rapport.inchanges} élève{rapport.inchanges > 1 ? 's' : ''} de votre fichier
                        correspondent déjà à TanoEd, il n'y a rien à modifier.
                    </p>
                </div>
                {blocDoublons ? (
                    <div className="max-w-md mx-auto bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                        {blocDoublons}
                    </div>
                ) : null}
                <div className="flex justify-center gap-3">
                    <button
                        onClick={onRetour}
                        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-4 py-2 rounded-xl transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Revenir aux classes
                    </button>
                </div>
            </div>
        );
    }

    if (importBloque) {
        return (
            <div className="border-2 border-red-300 bg-red-50 rounded-xl p-4">
                <p className="text-sm font-bold text-red-800 flex items-center gap-2 mb-1.5">
                    <AlertTriangle className="w-4 h-4" />
                    {avertissementsFiltres.length} ligne{avertissementsFiltres.length > 1 ? 's' : ''} à corriger avant d'importer
                </p>
                <ul className="text-xs text-red-800 space-y-0.5 max-h-40 overflow-y-auto mb-3">
                    {avertissementsFiltres.map((a, i) => (
                        <li key={i}>• {a}</li>
                    ))}
                </ul>
                <p className="text-xs text-red-700 mb-2.5">
                    Aucun import n'est possible tant que ces lignes ne sont pas corrigées.
                </p>
                <button
                    onClick={onRecommencer}
                    className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-500/30 transition-all"
                >
                    Corrigez ces lignes dans votre fichier Excel, puis ré-importez-le
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* ① Panier d'alerte À PART : sortants liés à un compte ENT actif */}
            {sortantsLies.length > 0 ? (
                <div className="border-2 border-red-300 bg-red-50 rounded-xl p-4">
                    <p className="text-sm font-bold text-red-800 flex items-center gap-2 mb-1.5">
                        <AlertTriangle className="w-4 h-4" />
                        {sortantsLies.length} élève{sortantsLies.length > 1 ? 's' : ''} utilise{sortantsLies.length > 1 ? 'nt' : ''} encore son compte ENT
                    </p>
                    <p className="text-xs text-red-700 mb-2">
                        Vérifiez qu'ils ont <span className="font-semibold">vraiment</span> quitté l'établissement
                        avant de confirmer : un élève encore présent perdrait l'accès à son espace.
                    </p>
                    <ul className="space-y-1">
                        {sortantsLies.map((s) => (
                            <li key={s.fiche_id} className="text-sm text-red-900 flex items-center gap-2">
                                <Link2 className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="font-semibold">{s.nom} {s.prenom}</span>
                                <span className="text-red-600 text-xs">({s.classe})</span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {/* ② Gardes (affichées qu'ICI, filtrées de la liste des avertissements) */}
            {rapport.garde_fichier_partiel ? (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3 text-sm text-red-800 font-medium">
                    🚨 <span className="font-bold">Votre fichier semble incomplet</span> :
                    {' '}{rapport.sortants.length} élève{rapport.sortants.length > 1 ? 's' : ''} disparaîtrai{rapport.sortants.length > 1 ? 'ent' : 't'} de TanoEd.
                    Avez-vous bien envoyé le fichier de <span className="font-bold">tout l'établissement</span> (toutes
                    les classes) ? Si non, revenez en arrière et refaites l'export Pronote complet.
                </div>
            ) : null}
            {rapport.garde_limite ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
                    ⚠️ Votre licence est limitée à {rapport.garde_limite.limite} élèves. Ce fichier en amènerait
                    {' '}{rapport.garde_limite.actifs_apres}. Décochez des classes ou contactez TanoEd.
                </div>
            ) : null}

            {/* ③ Le fichier a été reconnu : une ligne, pas des tuiles (les autres
                nombres sont dans le badge de chaque panneau, épreuve de Musk) */}
            <p className="text-sm text-slate-500 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                {rapport.inchanges} élève{rapport.inchanges > 1 ? 's' : ''} déjà à jour, rien à faire pour {rapport.inchanges > 1 ? 'eux' : 'lui'}.
            </p>

            {/* ⑤ Arbitrages : le panneau ne fait qu'annoncer et ouvrir le modal
                (décision user : tout le détail vit dans « un cas par un ») */}
            {nbDecisions > 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Scale className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <p className="text-sm font-semibold text-slate-800">
                            {nbDecisions} élève{nbDecisions > 1 ? 's' : ''} à confirmer
                        </p>
                        <Etat fait={resteAArbitrer === 0} libelleFait="✓ tout est répondu" />
                        <span className="ml-auto text-xs text-slate-400">
                            Rien n'est enregistré tant que vous n'avez pas validé.
                        </span>
                    </div>
                    {blocDoublons ? <div className="mt-2">{blocDoublons}</div> : null}
                    {/* Le récapitulatif = la CONCLUSION de chaque cas : l'histoire
                        choisie dans le modal (titreRecit de la carte élue) */}
                    <ul className="mt-3 space-y-1.5">
                        {casArbitrage.map((c) => {
                            const elu = c.repondu ? c.options.find((o) => o.value === c.valeur) : null;
                            const sujetNom = c.sujet || (c.sujetPill || '').split(' · ')[0];
                            return (
                                <li key={c.id} className="text-sm flex items-start gap-2">
                                    {c.repondu ? (
                                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                    ) : (
                                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                        </span>
                                    )}
                                    <span className="text-slate-700">
                                        <span className="font-medium">{sujetNom}</span>
                                        <span className="text-slate-400"> : </span>
                                        {c.repondu
                                            ? (elu?.titreRecit || 'décidé plus tard (aucune action)')
                                            : <span className="text-amber-700">à traiter</span>}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                        <button
                            onClick={() => setModalOuvert(true)}
                            className={`transition-colors ${
                                resteAArbitrer > 0
                                    ? 'w-full sm:w-auto px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30'
                                    : 'text-sm text-blue-600 hover:text-blue-800 font-semibold'
                            }`}
                        >
                            {resteAArbitrer > 0
                                ? `Traiter les cas un par un (${resteAArbitrer})`
                                : 'Revoir les réponses'}
                        </button>
                        {deplacementsSansReponse.length > 0 ? (
                            <button
                                onClick={confirmerDeplacements}
                                className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                            >
                                {deplacementsSansReponse.length > 1
                                    ? `Ce sont bien des changements de classe (${deplacementsSansReponse.length})`
                                    : 'C\'est bien un changement de classe'}
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : blocDoublons ? (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">{blocDoublons}</div>
            ) : null}

            {/* Modal guidé « un cas par un » — mêmes résolutions, l'inline suit en live */}
            {modalOuvert ? (
                <ModalArbitrage
                    cas={casArbitrage}
                    sorties={rapport.sortants.length + deltas.sortants}
                    onFermer={() => setModalOuvert(false)}
                />
            ) : null}

            {/* Verrou : tout doit être arbitré avant de continuer */}
            {resteAArbitrer > 0 ? (
                <p className="text-xs text-amber-700 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    Répondez aux {resteAArbitrer > 1 ? `${resteAArbitrer} cas restants` : 'cas restant'} pour continuer.
                </p>
            ) : null}

            {deplacesSurs.length > 0 ? (
                <Panier icone={ArrowRight} titre="Changements de classe" teinte="neutre" iconeClasse="text-blue-600" compte={deplacesSurs.length} repliable>
                    <ul className="space-y-1 max-h-56 overflow-y-auto pr-1">
                        {deplacesSurs.map((d, i) => (
                            <li key={d.ligne ?? i} className="text-sm text-slate-700 flex items-center gap-2 bg-white/70 rounded-lg px-3 py-1.5">
                                <span className="font-medium">{d.nom} {d.prenom}</span>
                                <span className="text-slate-400 text-xs ml-auto">{d.de_classe}</span>
                                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                                <span className="text-slate-600 text-xs font-semibold">{d.vers_classe}</span>
                            </li>
                        ))}
                    </ul>
                </Panier>
            ) : null}

            {/* ④ Sortants + confirmation obligatoire */}
            {rapport.sortants.length > 0 ? (
                <Panier icone={UserMinus} titre="Élèves qui quittent l'établissement (absents de votre fichier)" teinte="attention" compte={rapport.sortants.length} etat={sortantsCoches}>
                    <p className="text-xs text-amber-800 mb-2">
                        Ils seront retirés des listes. Leurs résultats sont conservés, et ils reviendront
                        automatiquement s'ils sont réimportés un jour.
                    </p>
                    <ul className="space-y-1 max-h-56 overflow-y-auto pr-1 mb-3">
                        {/* ENT liés d'abord (les cas sensibles en tête de liste), puis alpha */}
                        {[...rapport.sortants].sort((a, b) =>
                            (b.liee_gar ? 1 : 0) - (a.liee_gar ? 1 : 0)
                            || `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr')
                        ).map((s) => (
                            <li key={s.fiche_id} className="text-sm text-slate-700 flex items-center gap-2 bg-white/70 rounded-lg px-3 py-1.5">
                                <span className="font-medium">{s.nom} {s.prenom}</span>
                                {s.liee_gar ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold">
                                        <Link2 className="w-3 h-3" /> compte ENT lié
                                    </span>
                                ) : null}
                                <span className="text-amber-700 text-xs ml-auto">{s.classe}</span>
                            </li>
                        ))}
                    </ul>
                    <label className="flex items-start gap-2.5 bg-white border border-amber-300 rounded-xl p-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={sortantsCoches}
                            onChange={(e) => setSortantsCoches(e.target.checked)}
                            className="w-4 h-4 accent-amber-600 mt-0.5 flex-shrink-0"
                        />
                        <span className="text-sm text-slate-700 font-medium">
                            {rapport.sortants.length > 1
                                ? `Je confirme que ces ${rapport.sortants.length} élèves ont quitté l'établissement.`
                                : 'Je confirme que cet élève a quitté l\'établissement.'}
                        </span>
                    </label>
                </Panier>
            ) : null}

            {rapport.crees.length > 0 ? (
                <Panier icone={UserPlus} titre="Nouveaux élèves" teinte="neutre" iconeClasse="text-emerald-600" compte={rapport.crees.length} repliable>
                    <ul className="space-y-1 max-h-56 overflow-y-auto pr-1">
                        {rapport.crees.map((c, i) => (
                            <li key={i} className="text-sm text-slate-700 flex items-center gap-2 bg-white/70 rounded-lg px-3 py-1.5">
                                <span className="font-medium">{c.nom} {c.prenom}</span>
                                <span className="text-slate-500 text-xs ml-auto">{c.classe}</span>
                            </li>
                        ))}
                    </ul>
                </Panier>
            ) : null}

            {rapport.reactives.length > 0 ? (
                <Panier icone={RotateCcw} titre="Élèves de retour (avaient quitté l'établissement)" teinte="neutre" iconeClasse="text-slate-500" compte={rapport.reactives.length} repliable>
                    <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
                        {rapport.reactives.map((r, i) => (
                            <li key={i} className="text-sm text-slate-700 flex items-center gap-2 bg-white/70 rounded-lg px-3 py-1.5">
                                <span className="font-medium">{r.nom} {r.prenom}</span>
                                <span className="text-slate-500 text-xs ml-auto">→ {r.vers_classe}</span>
                            </li>
                        ))}
                    </ul>
                </Panier>
            ) : null}

            {/* Rapport périmé : les réponses données peuvent changer les paniers
                (ex. « 2 élèves » ajoute un quittant) — la validation attend un
                rapport à jour, sinon le back refuserait l'apply. Le mot
                « vérification » est réservé au nom de l'étape : le bouton dit ce
                qu'on obtient (les listes avec ses réponses), pas ce qu'il fait. */}
            {rapportPerime && resteAArbitrer === 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800 flex items-start gap-2">
                    <RefreshCw className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Vos réponses changent les listes : un élève qui quitte l'établissement peut s'y ajouter.</span>
                </div>
            ) : null}

            {/* Erreur d'application renvoyée par le serveur */}
            {erreur ? (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 text-sm text-red-800">
                    <span className="font-semibold">Enregistrement refusé : </span>{erreur}
                </div>
            ) : null}

            {/* Actions */}
            <div className="flex flex-wrap justify-between items-center gap-3 pt-1">
                <button
                    onClick={onRetour}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-3 py-2 rounded-xl transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Revenir aux classes
                </button>
                <div className="flex items-center gap-2">
                    {rapportPerime ? (
                        <button
                            onClick={onReverifier}
                            disabled={chargement || resteAArbitrer > 0}
                            title={resteAArbitrer > 0 ? 'Répondez d\'abord à tous les cas à confirmer' : undefined}
                            className={`px-6 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all ${
                                chargement || resteAArbitrer > 0
                                    ? 'bg-slate-300 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/30'
                            }`}
                        >
                            {chargement ? 'Calcul...' : 'Voir les listes avec mes réponses'}
                        </button>
                    ) : (
                        <button
                            onClick={onAppliquer}
                            disabled={applyBloque}
                            title={resteAArbitrer > 0
                                ? 'Répondez d\'abord à tous les cas à confirmer'
                                : rapport.sortants.length > 0 && !sortantsCoches
                                    ? "Cochez d'abord la confirmation des élèves partis" : undefined}
                            className={`px-6 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all ${
                                applyBloque
                                    ? 'bg-slate-300 cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/30'
                            }`}
                        >
                            {chargement ? 'Enregistrement...' : libelleValider}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default EtapeRapport;
